import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Enso } from "../components/SacredGeometry";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, BookOpen, Plus, Trash2, Edit3, Save, X, Layers, GraduationCap } from "lucide-react";

export default function Admin() {
  const [tab, setTab] = useState("subscribers");
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [lessonForm, setLessonForm] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const openCourse = async (courseId) => {
    const { data } = await api.get(`/courses/${courseId}`);
    setSelectedCourse(data);
  };

  const saveLesson = async () => {
    try {
      if (lessonForm.id) {
        await api.put(`/admin/lessons/${lessonForm.id}`, lessonForm);
      } else {
        await api.post("/admin/lessons", lessonForm);
      }
      toast.success("Teaching saved.");
      setLessonForm(null);
      await openCourse(selectedCourse.id);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteLesson = async (id) => {
    try {
      await api.delete(`/admin/lessons/${id}`);
      toast.success("Teaching removed.");
      await openCourse(selectedCourse.id);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const newLesson = () => setLessonForm({
    course_id: selectedCourse.id, title: "", subtitle: "", video_url: "https://www.youtube.com/embed/inpok4MKVLM",
    pdf_url: "", content: "", hand_positions: [], is_preview: false, order: (selectedCourse.lessons.length || 0) + 1,
  });

  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="rounded-2xl bg-sanctuary-card gold-border p-6">
      <div className="w-10 h-10 rounded-full bg-gold/10 grid place-items-center text-gold mb-4"><Icon size={18} /></div>
      <div className="font-display text-4xl text-parchment">{value ?? "—"}</div>
      <div className="text-sm text-text-muted mt-1">{label}</div>
    </div>
  );

  return (
    <div className="min-h-screen pt-28 pb-24 bg-sanctuary-deep">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-10">
          <Enso className="w-12 h-12 text-gold" />
          <div>
            <div className="eyebrow mb-1">Sanctuary Guardian</div>
            <h1 className="font-display text-4xl text-parchment font-light">Temple Administration</h1>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          <StatCard icon={Users} label="Students" value={stats?.total_students} />
          <StatCard icon={GraduationCap} label="Active Members" value={stats?.active_subscribers} />
          <StatCard icon={Layers} label="Degrees" value={stats?.courses} />
          <StatCard icon={BookOpen} label="Teachings" value={stats?.lessons} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-sanctuary-card gold-border mb-8">
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content">Curriculum</TabsTrigger>
          </TabsList>

          <TabsContent value="subscribers">
            <div className="rounded-2xl bg-sanctuary-card gold-border overflow-hidden" data-testid="admin-subscribers-table">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gold/12 text-xs uppercase tracking-widest text-text-muted">
                    <th className="p-4">Name</th>
                    <th className="p-4">Email</th>
                    <th className="p-4">Membership</th>
                    <th className="p-4">Seals</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-text-muted">No students have joined yet.</td></tr>
                  )}
                  {subscribers.map((s) => (
                    <tr key={s.id} className="border-b border-gold/8 hover:bg-gold/5 transition-colors">
                      <td className="p-4 text-parchment">{s.name}</td>
                      <td className="p-4 text-text-muted text-sm">{s.email}</td>
                      <td className="p-4">
                        {s.subscription?.status === "active" ? (
                          <span className="text-xs px-3 py-1 rounded-full bg-sage/15 text-sage-light border border-sage/30 capitalize">{s.subscription.plan}</span>
                        ) : (
                          <span className="text-xs px-3 py-1 rounded-full bg-sanctuary-surface text-text-dim">guest</span>
                        )}
                      </td>
                      <td className="p-4 text-gold text-sm">{s.progress?.certificates?.length || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="content">
            {!selectedCourse ? (
              <div className="grid md:grid-cols-2 gap-5">
                {courses.map((c) => (
                  <button key={c.id} onClick={() => openCourse(c.id)} className="text-left rounded-2xl bg-sanctuary-card gold-border p-6 hover:border-gold/40 transition-colors" data-testid={`admin-course-${c.order}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-jp text-3xl text-gold/40">{c.japanese_name}</span>
                      <div>
                        <div className="eyebrow mb-1">{c.level_label}</div>
                        <div className="font-display text-2xl text-parchment">{c.title}</div>
                      </div>
                    </div>
                    <div className="text-sm text-text-muted mt-3">{c.lesson_count} teachings · tap to manage</div>
                  </button>
                ))}
              </div>
            ) : (
              <div>
                <button onClick={() => { setSelectedCourse(null); setLessonForm(null); }} className="text-sm text-text-muted hover:text-gold mb-6 transition-colors" data-testid="admin-back-courses">← All degrees</button>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-3xl text-parchment">{selectedCourse.title}</h2>
                  <button onClick={newLesson} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="admin-add-lesson-button">
                    <Plus size={16} /> Add teaching
                  </button>
                </div>

                {lessonForm && (
                  <div className="rounded-2xl bg-sanctuary-surface gold-border p-6 mb-6" data-testid="admin-lesson-form">
                    <div className="flex items-center justify-between mb-4">
                      <div className="eyebrow">{lessonForm.id ? "Edit teaching" : "New teaching"}</div>
                      <button onClick={() => setLessonForm(null)} className="text-text-muted hover:text-parchment"><X size={18} /></button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input placeholder="Title" value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} className="bg-sanctuary-card gold-border rounded-lg px-4 py-2.5 text-parchment outline-none focus:border-gold/50" data-testid="lesson-form-title" />
                      <input placeholder="Subtitle" value={lessonForm.subtitle} onChange={(e) => setLessonForm({ ...lessonForm, subtitle: e.target.value })} className="bg-sanctuary-card gold-border rounded-lg px-4 py-2.5 text-parchment outline-none focus:border-gold/50" />
                      <input placeholder="Video embed URL" value={lessonForm.video_url} onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })} className="bg-sanctuary-card gold-border rounded-lg px-4 py-2.5 text-parchment outline-none focus:border-gold/50" />
                      <input placeholder="PDF URL" value={lessonForm.pdf_url} onChange={(e) => setLessonForm({ ...lessonForm, pdf_url: e.target.value })} className="bg-sanctuary-card gold-border rounded-lg px-4 py-2.5 text-parchment outline-none focus:border-gold/50" />
                    </div>
                    <textarea placeholder="Teaching content" rows={5} value={lessonForm.content} onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })} className="w-full mt-4 bg-sanctuary-card gold-border rounded-lg px-4 py-2.5 text-parchment outline-none focus:border-gold/50" />
                    <div className="flex items-center gap-4 mt-4">
                      <label className="flex items-center gap-2 text-sm text-text-muted">
                        <input type="checkbox" checked={lessonForm.is_preview} onChange={(e) => setLessonForm({ ...lessonForm, is_preview: e.target.checked })} className="accent-gold" /> Free preview
                      </label>
                      <input type="number" placeholder="Order" value={lessonForm.order} onChange={(e) => setLessonForm({ ...lessonForm, order: Number(e.target.value) })} className="w-24 bg-sanctuary-card gold-border rounded-lg px-3 py-2 text-parchment outline-none" />
                      <button onClick={saveLesson} className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="lesson-form-save"><Save size={16} /> Save</button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {selectedCourse.lessons.map((l, i) => (
                    <div key={l.id} className="flex items-center gap-4 rounded-xl bg-sanctuary-card gold-border p-4" data-testid={`admin-lesson-row-${i}`}>
                      <span className="w-8 h-8 rounded-full bg-gold/10 grid place-items-center text-gold text-sm shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg text-parchment truncate">{l.title}</div>
                        <div className="text-sm text-text-muted truncate">{l.subtitle}</div>
                      </div>
                      {l.is_preview && <span className="text-[0.6rem] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/15 text-gold">preview</span>}
                      <button onClick={() => setLessonForm(l)} className="text-text-muted hover:text-gold transition-colors" data-testid={`admin-edit-lesson-${i}`}><Edit3 size={16} /></button>
                      <button onClick={() => deleteLesson(l.id)} className="text-text-muted hover:text-red-400 transition-colors" data-testid={`admin-delete-lesson-${i}`}><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
